import { type Unsubscribe } from "redux";
import { type ThunkDispatch } from "redux-thunk";
import { debounce } from "throttle-debounce";
import { isKeyOf } from "../../../../types/internal/utility";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import { userLogout } from "../../../Actions/Users/UserActions";
import { type SaleState } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import UUID from "../../Generic/UUID";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { type OptionValue, subscribeToOption, unsubscribeFromOption } from "../../Platform/Options/Options";

/**
 * Function to validate the value of the current_sale localeStorage option
 */
function isSaleInProcess(sale: OptionValue): boolean {
    if(typeof sale === "object" && sale !== null && !Array.isArray(sale)) {
        if(isKeyOf(sale, "products") && isKeyOf(sale, "customer")) {
            if(Array.isArray(sale.products) && sale.products.length) {
                return true;
            } else if(sale.customer) {
                return true;
            }
        }
    }

    return false;
}

const updateTimer = debounce(500, () => {
    if(!AutoLogout.globallyActive) {
        return;
    }

    localStorage.setItem(AutoLogout.eventKey, Date.now().toString());
});

export default class AutoLogout {
    private INTERVAL_TIMER = 1000;
    protected saleInProgress = false;
    protected logoutDuringSale = false;
    protected interval: null | ReturnType<typeof setInterval> = null;

    protected timeUntilLogout = 0;
    protected isOffline = false;
    protected storeSubscription: Unsubscribe | undefined;
    protected isLoggedIn = false;
    protected overrideTime: false | number;
    protected isCreated = false;
    protected id = UUID.generate();

    public static eventKey = "_logout_last_event_time";
    public static globallyActive = false;
    protected static registeredLogouts = new Set<AutoLogout>();

    constructor(overrideTime: number | false = false) {
        this.overrideTime = overrideTime;

        document.addEventListener("visibilitychange", this.handleVisibilityChange, {
            capture: false,
            passive: true,
            once   : false,
        });

        const value = subscribeToOption("auto_logout_time", this.handleLogoutTimeChange);

        this.handleLogoutTimeChange("auto_logout_time", value);

        AutoLogout.registeredLogouts.add(this);
    }

    public deconstruct(): void {
        this.destroy();
        unsubscribeFromOption("auto_logout_time", this.handleLogoutTimeChange);
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        AutoLogout.registeredLogouts.delete(this);
    }

    public static setupListeners = (): void => {
        AutoLogout.globallyActive = true;

        const eventSettings = { capture: false, passive: true, once: false };

        document.addEventListener("mousemove", updateTimer, eventSettings);
        document.addEventListener("click", updateTimer, eventSettings);
        document.addEventListener("keyup", updateTimer, eventSettings);
        window.addEventListener("focus", this.handleWindowFocus, eventSettings);
        window.addEventListener("blur", this.handleWindowBlur, eventSettings);
    };

    public static removeListeners = (): void => {
        document.removeEventListener("mousemove", updateTimer);
        document.removeEventListener("click", updateTimer);
        document.removeEventListener("keyup", updateTimer);
        window.removeEventListener("focus", this.handleWindowFocus);
        window.removeEventListener("blur", this.handleWindowBlur);

        AutoLogout.globallyActive = false;
    };

    public static updateTimeEvent = (time: number): void => {
        localStorage.setItem(this.eventKey, time.toString());
    };

    public setupInterval = (): void => {
        this.interval = setInterval(this.check, this.INTERVAL_TIMER);
    };

    public subscribeToEvents = (): void => {
        const logoutDuringSale = subscribeToOption("auto_logout_during_sale", this.handleLogoutDuringSaleChange);

        this.handleLogoutDuringSaleChange("auto_logout_during_sale", logoutDuringSale);

        const sale = subscribeToOption("current_sale", this.handleSaleChange);

        this.handleSaleChange("current_sale", sale);

        subscribeToOption("last_login_time", updateTimer);

        this.storeSubscription = store.subscribe(this.updateFromState);
    };

    public unsubscribeFromEvents = (): void => {
        unsubscribeFromOption("auto_logout_during_sale", this.handleLogoutDuringSaleChange);
        unsubscribeFromOption("current_sale", this.handleSaleChange);
        unsubscribeFromOption("last_login_time", updateTimer);

        if(typeof this.storeSubscription === "function") {
            this.storeSubscription();
        }
    };

    protected updateFromState = (): void => {
        const currentState = store.getState();

        this.isOffline = !currentState.information.online;

        const wasLoggedIn = this.isLoggedIn;

        this.isLoggedIn = currentState.user.id !== false;

        if(wasLoggedIn !== this.isLoggedIn) {
            updateTimer();
        }
    };

    public handleLogoutTimeChange = (_option: "auto_logout_time", value: number): void => {
        if(this.overrideTime !== false) {
            value = this.overrideTime;
        }

        // Make sure that the minimum time is 10 seconds
        if(value > 0 && value < 10) {
            value = 10;
        } else if(value === 0) {
            // Set default value to 2 hours
            value = 2 * 60 * 60;
        }

        const old = this.timeUntilLogout;

        this.timeUntilLogout = value * 1000;

        if(value === 0) {
            this.destroy();
        } else if(old === 0) {
            this.create();
        }
    };

    public handleLogoutDuringSaleChange = (_option: "auto_logout_during_sale", value: boolean): void => {
        if(this.overrideTime !== false) {
            this.logoutDuringSale = true;
        } else {
            this.logoutDuringSale = value;
        }
    };

    public handleSaleChange = (_option: "current_sale", sale: null | SaleState): void => {
        this.saleInProgress = isSaleInProcess(sale);
    };

    public handleVisibilityChange = (): void => {
        if(document) {
            if(document.hidden) {
                AutoLogout.globallyActive = false;
                this.destroy();
            } else {
                this.create();
            }
        }
    };

    protected static handleWindowFocus = (): void => {
        AutoLogout.globallyActive = true;
        AutoLogout.registeredLogouts.forEach(logout => {
            logout.check();
            logout.create();
        });
    };

    protected static handleWindowBlur = (): void => {
        AutoLogout.globallyActive = false;
        AutoLogout.registeredLogouts.forEach(logout => logout.destroy());
    };

    public create = (): void => {
        if(this.isCreated) {
            return;
        }

        this.isCreated = true;
        this.subscribeToEvents();
        this.setupInterval();
    };

    public destroy = (): void => {
        if(!this.isCreated) {
            return;
        }

        this.isCreated = false;

        if(this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }

        this.unsubscribeFromEvents();
    };

    public check = (): void => {
        if(!AutoLogout.globallyActive) {
            return;
        }

        if(this.saleInProgress && !this.logoutDuringSale) {
            return;
        }

        if(this.isOffline) {
            return;
        }

        if(!this.isLoggedIn) {
            return;
        }

        if(this.timeUntilLogout === 0) {
            return;
        }

        const lastEvent = parseInt(localStorage.getItem(AutoLogout.eventKey) ?? "0");

        if(!lastEvent) {
            return;
        }

        const now = Date.now();
        const timeLeft = lastEvent + this.timeUntilLogout;
        const difference = timeLeft - now;

        if(difference < 0) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log(`Logging out due to auto logout (${this.id}) setting, difference: ${difference}`);
            }

            (store.dispatch as ThunkDispatch<any, any, any>)(userLogout());
            updateTimer();

            return;
        }
    };
}
